import {THREE} from '../three-defs.js';

import * as shaders from '../../game/render/shaders.js';

import * as entity from "../entity.js";
import * as math from '../math.js';

import * as grass_component from './grass-component.js';
import {GenerateHeightField} from './terrain-noise.js';


// ---------------------------------------------------------------------------
// Sliding-window infinite terrain. The land is a pure FBM function of world
// position (terrain-noise.js); this component caches a dims x dims window of
// it - one float texture for the GPU (terrain displacement happens in the
// vertex shader; grass blades sample it too) and one Float32Array for the
// CPU (collision, grass placement). When the player strays far enough from
// the window centre, a worker regenerates the window around them and
// everything swaps atomically: same function, same lattice, so overlapping
// regions are bit-identical and the ground never visibly moves.
// ---------------------------------------------------------------------------

const TERRAIN_HEIGHT = 180;
const TERRAIN_OFFSET = 118;

const TERRAIN_DIMS = 4000;
const HEIGHTMAP_RES = 1024;

// Re-centre when the player is this far from the window origin. The far
// edge stays at least dims/2 - this away - deep inside the fog.
const RECENTER_DIST = 500;

// Window origins snap to the sampling lattice so consecutive windows agree
// exactly where they overlap.
const GRID_STEP = TERRAIN_DIMS / (HEIGHTMAP_RES - 1);

// Far shell: a coarse second terrain ring carrying honest skylines out to
// ~12km, so the day fog no longer has to curtain off the near window's
// edge. Same eroded field truncated to 5 octaves (bit-identical macro -
// see GenerateHeightField); inside the near window it ducks below the
// detailed terrain, and its outer rim sinks beneath the horizon haze.
const FAR_DIMS = 24000;
const FAR_RES = 512;

// Both terrain surfaces are built as 8x8 chunk grids so three's frustum
// culling can drop everything outside the view - as single meshes with
// frustumCulled=false, all ~3M vertices rendered every frame regardless
// of where the camera looked. Chunk bounding spheres are set by hand
// (displacement happens in the vertex shader, so computed bounds would
// be the flat plane's). Vertical bounds are conservative: the full
// height range, plus rim drop and duck for the shell.
const TERRAIN_CHUNKS = 8;
const FAR_OCTAVES = 5;
const FAR_RECENTER_DIST = 2000;
const FAR_GRID_STEP = FAR_DIMS / (FAR_RES - 1);
// Duck depth must exceed the detail octaves' amplitude so the shell never
// pokes through the near terrain's dips.
const SHELL_DUCK = 40;
const SHELL_RIM_START = 10000;     // local chebyshev dist where the rim sink begins
const SHELL_RIM_WIDTH = 1800;
const SHELL_RIM_DROP = 250;

class Heightmap {
  constructor(resolution, dims, height) {
    this.res_ = resolution;
    this.dims_ = dims;
    this.height_ = height;
    this.data_ = null;
    this.originX_ = 0;
    this.originZ_ = 0;
  }

  SetData(f32, originX, originZ) {
    this.data_ = f32;
    this.originX_ = originX;
    this.originZ_ = originZ;
  }

  // Bilinear sample, uv in [0, 1], matching GPU texture2D(heightmap, uv).x.
  Get(u, v) {
    const n = this.res_ - 1;
    const x = math.sat(u) * n;
    const y = math.sat(v) * n;

    const x1 = Math.floor(x);
    const y1 = Math.floor(y);
    const x2 = math.clamp(x1 + 1, 0, n);
    const y2 = math.clamp(y1 + 1, 0, n);
    const xp = x - x1;
    const yp = y - y1;

    const d = this.data_;
    const r = this.res_;
    const p11 = d[y1 * r + x1];
    const p21 = d[y1 * r + x2];
    const p12 = d[y2 * r + x1];
    const p22 = d[y2 * r + x2];

    const px1 = math.lerp(xp, p11, p21);
    const px2 = math.lerp(xp, p12, p22);

    return math.lerp(yp, px1, px2) * this.height_;
  }

  // World-space sample; same uv convention as the terrain/grass shaders.
  GetWorld(x, z) {
    const u = (x - this.originX_) / this.dims_ + 0.5;
    const v = 1 - ((z - this.originZ_) / this.dims_ + 0.5);
    return this.Get(u, v);
  }
}


export class TerrainComponent extends entity.Component {
  static CLASS_NAME = 'TerrainComponent';

  get NAME() {
    return TerrainComponent.CLASS_NAME;
  }

  #params_;
  #heightmap_;
  #mesh_;
  #texture_;
  #origin_;
  #worker_;
  #regenPending_;
  #farMesh_;
  #farTexture_;
  #farOrigin_;
  #farRegenPending_;
  #shellNearParams_;
  #terrainMaterial_;
  #farMaterial_;

  constructor(params) {
    super();

    this.#params_ = params;
    this.#heightmap_ = null;
    this.#mesh_ = null;
    this.#texture_ = null;
    this.#origin_ = new THREE.Vector2(0, 0);
    this.#worker_ = null;
    this.#regenPending_ = false;
    this.#farMesh_ = null;
    this.#farTexture_ = null;
    this.#farOrigin_ = new THREE.Vector2(0, 0);
    this.#farRegenPending_ = false;
    // (nearOriginX, nearOriginZ, duckRampStart, duckDepth) - shared with
    // the shell material and mutated in place on near re-centres.
    this.#shellNearParams_ = new THREE.Vector4(
        0, 0, TERRAIN_DIMS * 0.5 - 500, SHELL_DUCK);
  }

  Destroy() {
    this.#mesh_.removeFromParent();
    if (this.#farMesh_) {
      this.#farMesh_.removeFromParent();
    }
    if (this.#worker_) {
      this.#worker_.terminate();
    }
  }

  GetHeight(x, y) {
    return this.#heightmap_.GetWorld(x, y) - TERRAIN_OFFSET;
  }

  IsReady() {
    return this.#heightmap_ != null;
  }

  // Builds a dims x dims surface as a TERRAIN_CHUNKS^2 grid of planes
  // sharing `material`, each chunk with a hand-set bounding sphere over
  // [yMin, yMax] so frustum culling works despite vertex-shader
  // displacement. The returned group is positioned at the window origin
  // on re-centres; chunk offsets are baked into the geometry so
  // position.xz + heightmapOrigin stays the world position the shaders
  // expect.
  #BuildChunkedSurface_(dims, segmentsPerChunk, material, yMin, yMax) {
    const group = new THREE.Group();
    const chunkSize = dims / TERRAIN_CHUNKS;
    const radius = Math.hypot(
        chunkSize * Math.SQRT1_2, (yMax - yMin) * 0.5) + 1.0;
    for (let cz = 0; cz < TERRAIN_CHUNKS; ++cz) {
      for (let cx = 0; cx < TERRAIN_CHUNKS; ++cx) {
        const ox = (cx + 0.5 - TERRAIN_CHUNKS * 0.5) * chunkSize;
        const oz = (cz + 0.5 - TERRAIN_CHUNKS * 0.5) * chunkSize;
        const geo = new THREE.PlaneGeometry(
            chunkSize, chunkSize, segmentsPerChunk, segmentsPerChunk);
        geo.rotateX(-Math.PI / 2);
        geo.translate(ox, 0, oz);
        geo.boundingSphere = new THREE.Sphere(
            new THREE.Vector3(ox, (yMin + yMax) * 0.5, oz), radius);
        const mesh = new THREE.Mesh(geo, material);
        mesh.receiveShadow = true;
        mesh.castShadow = false;
        group.add(mesh);
      }
    }
    return group;
  }

  InitEntity() {
    const threejs = this.FindEntity('threejs').GetComponent('ThreeJSController');

    // Initial window, generated synchronously at load.
    const field = GenerateHeightField(HEIGHTMAP_RES, TERRAIN_DIMS, 0, 0);

    this.#heightmap_ = new Heightmap(HEIGHTMAP_RES, TERRAIN_DIMS, TERRAIN_HEIGHT);
    this.#heightmap_.SetData(field.f32, 0, 0);

    // R16F: filterable everywhere WebGL2 runs, and far more height
    // precision than 8 bits.
    this.#texture_ = new THREE.DataTexture(
        field.u16, HEIGHTMAP_RES, HEIGHTMAP_RES, THREE.RedFormat, THREE.HalfFloatType);
    this.#texture_.minFilter = THREE.LinearFilter;
    this.#texture_.magFilter = THREE.LinearFilter;
    this.#texture_.wrapS = THREE.ClampToEdgeWrapping;
    this.#texture_.wrapT = THREE.ClampToEdgeWrapping;
    this.#texture_.needsUpdate = true;

    // The planes stay flat forever; the vertex shader displaces them from
    // the height window, so re-centring is a group move + texture swap.
    // 8 chunks x 128 segments puts a vertex on every heightmap texel:
    // the rendered silhouette matches the surface grass/collision use
    // exactly (coarser meshes undersampled the field, opening a
    // phantom-sky band at crests). Mobile halves it.
    const isMobile = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;
    const segsPerChunk = isMobile ? 64 : 128;

    const textureLoader = new THREE.TextureLoader();
    const grid = textureLoader.load('./textures/grid.png');
    grid.magFilter = THREE.LinearFilter;
    grid.minFilter = THREE.LinearMipMapLinearFilter;
    grid.wrapS = THREE.RepeatWrapping;
    grid.wrapT = THREE.RepeatWrapping;
    grid.anisotropy = 16;

    const terrainMaterial = new shaders.GamePBRMaterial('TERRAIN', {});
    terrainMaterial.setTexture('heightmap', this.#texture_);
    terrainMaterial.setTexture('grid', grid);
    terrainMaterial.setVec4('heightParams', new THREE.Vector4(TERRAIN_DIMS, TERRAIN_DIMS, TERRAIN_HEIGHT, TERRAIN_OFFSET));
    // Shared Vector2: mutating it on re-centre updates every material that
    // holds it (terrain + all grass tiers) in one place.
    terrainMaterial.setVec2('heightmapOrigin', this.#origin_);

    // Placeholder until the cloud noise texture is generated (see
    // grass-component.js - the sampler always needs a binding).
    const cloudPlaceholder = new THREE.Data3DTexture(
        new Uint8Array([0, 0, 0, 255]), 1, 1, 1);
    cloudPlaceholder.format = THREE.RGBAFormat;
    cloudPlaceholder.needsUpdate = true;
    terrainMaterial.setTexture('cloudShadowNoise', cloudPlaceholder);
    terrainMaterial.setFloat('cloudShadowTime', 0);
    terrainMaterial.setFloat('cloudShadowReady', 0);

    // Atmosphere defaults (night) until the LUTs are ready - the fog in
    // sky.glsl samples these, so they must be bound from first compile.
    const atmoPlaceholder = new THREE.DataTexture(
        new Uint8Array([0, 0, 0, 255]), 1, 1, THREE.RGBAFormat);
    atmoPlaceholder.needsUpdate = true;
    terrainMaterial.setTexture('uAtmoSkyViewLUT', atmoPlaceholder);
    terrainMaterial.setVec3('uAtmoSunDir', new THREE.Vector3(0, -1, 0));
    terrainMaterial.setFloat('uAtmoDayFactor', 0);
    terrainMaterial.setFloat('uAtmoViewHeight', 6360.002);
    const moonDefault = new THREE.Vector3(-1, 0.45, 1).normalize();
    terrainMaterial.setVec3('uMoonDir', moonDefault);
    terrainMaterial.setVec3('uShadowLightDir', moonDefault);

    // Shell shaping is inert on the near mesh (duck and rim both zero).
    terrainMaterial.setVec4('uShellParams', new THREE.Vector4(0, 0, 0, 0));
    terrainMaterial.setVec4('uShellRim', new THREE.Vector4(0, 1, 0, 0));

    this.#terrainMaterial_ = terrainMaterial;
    this.#mesh_ = this.#BuildChunkedSurface_(
        TERRAIN_DIMS, segsPerChunk, terrainMaterial,
        -TERRAIN_OFFSET, TERRAIN_HEIGHT - TERRAIN_OFFSET);
    this.#mesh_.position.set(0, 0, 0);

    threejs.AddSceneObject(this.#mesh_);

    // ---- Far shell -------------------------------------------------------
    const farField = GenerateHeightField(
        FAR_RES, FAR_DIMS, 0, 0, FAR_OCTAVES);
    this.#farTexture_ = new THREE.DataTexture(
        farField.u16, FAR_RES, FAR_RES, THREE.RedFormat, THREE.HalfFloatType);
    this.#farTexture_.minFilter = THREE.LinearFilter;
    this.#farTexture_.magFilter = THREE.LinearFilter;
    this.#farTexture_.wrapS = THREE.ClampToEdgeWrapping;
    this.#farTexture_.wrapT = THREE.ClampToEdgeWrapping;
    this.#farTexture_.needsUpdate = true;

    const farMaterial = new shaders.GamePBRMaterial('TERRAIN', {});
    farMaterial.setTexture('heightmap', this.#farTexture_);
    farMaterial.setTexture('grid', grid);
    farMaterial.setVec4('heightParams', new THREE.Vector4(
        FAR_DIMS, FAR_DIMS, TERRAIN_HEIGHT, TERRAIN_OFFSET));
    farMaterial.setVec2('heightmapOrigin', this.#farOrigin_);
    farMaterial.setTexture('cloudShadowNoise', cloudPlaceholder);
    farMaterial.setFloat('cloudShadowTime', 0);
    farMaterial.setFloat('cloudShadowReady', 0);
    farMaterial.setTexture('uAtmoSkyViewLUT', atmoPlaceholder);
    farMaterial.setVec3('uAtmoSunDir', new THREE.Vector3(0, -1, 0));
    farMaterial.setFloat('uAtmoDayFactor', 0);
    farMaterial.setFloat('uAtmoViewHeight', 6360.002);
    farMaterial.setVec3('uMoonDir', moonDefault);
    farMaterial.setVec3('uShadowLightDir', moonDefault);
    farMaterial.setVec4('uShellParams', this.#shellNearParams_);
    farMaterial.setVec4('uShellRim', new THREE.Vector4(
        SHELL_RIM_START, SHELL_RIM_WIDTH, SHELL_RIM_DROP, 0));

    // Vertical bounds include the shell's rim drop and near-window duck.
    this.#farMaterial_ = farMaterial;
    this.#farMesh_ = this.#BuildChunkedSurface_(
        FAR_DIMS, segsPerChunk, farMaterial,
        -TERRAIN_OFFSET - SHELL_RIM_DROP - SHELL_DUCK,
        TERRAIN_HEIGHT - TERRAIN_OFFSET);
    this.#farMesh_.position.set(0, 0, 0);

    threejs.AddSceneObject(this.#farMesh_);

    this.#worker_ = new Worker(
        new URL('./terrain-worker.js', import.meta.url), {type: 'module'});
    this.#worker_.onmessage = (e) => { this.#OnWindowReady_(e.data); };

    this.Broadcast({
      topic: 'render.loaded',
      value: this.#mesh_,
    });

    const grass = new entity.Entity();
    grass.AddComponent(new grass_component.GrassComponent({
      terrain: this.Parent,
      height: TERRAIN_HEIGHT,
      offset: TERRAIN_OFFSET,
      dims: TERRAIN_DIMS,
      heightmap: this.#texture_,
      heightmapOrigin: this.#origin_,
    }));
    grass.SetActive(true);
    grass.Init(this.Parent);
  }

  #OnWindowReady_(data) {
    if (data.kind === 'far') {
      this.#farRegenPending_ = false;
      this.#farTexture_.image.data = data.u16;
      this.#farTexture_.needsUpdate = true;
      this.#farOrigin_.set(data.originX, data.originZ);
      this.#farMesh_.position.set(data.originX, 0, data.originZ);
      return;
    }

    this.#regenPending_ = false;

    // Atomic swap: CPU heights, GPU texture, shared origin uniform, and
    // mesh position all change in the same frame. Heights on the shared
    // lattice are identical between the windows, so nothing visibly moves.
    this.#heightmap_.SetData(data.f32, data.originX, data.originZ);
    this.#texture_.image.data = data.u16;
    this.#texture_.needsUpdate = true;
    this.#origin_.set(data.originX, data.originZ);
    this.#mesh_.position.set(data.originX, 0, data.originZ);
    // The shell's duck region follows the near window.
    this.#shellNearParams_.x = data.originX;
    this.#shellNearParams_.y = data.originZ;
  }

  Update(timeElapsed) {
    if (!this.#mesh_) {
      return;
    }

    const player = this.FindEntity('player');
    if (player && !this.#regenPending_) {
      const dx = player.Position.x - this.#origin_.x;
      const dz = player.Position.z - this.#origin_.y;
      if (dx * dx + dz * dz > RECENTER_DIST * RECENTER_DIST) {
        this.#regenPending_ = true;
        this.#worker_.postMessage({
          resolution: HEIGHTMAP_RES,
          dims: TERRAIN_DIMS,
          originX: Math.round(player.Position.x / GRID_STEP) * GRID_STEP,
          originZ: Math.round(player.Position.z / GRID_STEP) * GRID_STEP,
          octaves: undefined,
          kind: 'near',
        });
      }
    }
    if (player && !this.#farRegenPending_) {
      const dx = player.Position.x - this.#farOrigin_.x;
      const dz = player.Position.z - this.#farOrigin_.y;
      if (dx * dx + dz * dz > FAR_RECENTER_DIST * FAR_RECENTER_DIST) {
        this.#farRegenPending_ = true;
        this.#worker_.postMessage({
          resolution: FAR_RES,
          dims: FAR_DIMS,
          originX: Math.round(player.Position.x / FAR_GRID_STEP) * FAR_GRID_STEP,
          originZ: Math.round(player.Position.z / FAR_GRID_STEP) * FAR_GRID_STEP,
          octaves: FAR_OCTAVES,
          kind: 'far',
        });
      }
    }

    const threejs = this.FindEntity('threejs').GetComponent('ThreeJSController');
    const materials = [this.#terrainMaterial_];
    if (this.#farMaterial_) {
      materials.push(this.#farMaterial_);
    }

    const cloudShadow = threejs.cloudShadow_;
    if (cloudShadow && cloudShadow.texture) {
      for (const m of materials) {
        m.setTexture('cloudShadowNoise', cloudShadow.texture);
        m.setFloat('cloudShadowTime', cloudShadow.time);
        m.setFloat('cloudShadowReady', 1);
      }
    }

    const atmo = threejs.atmosphereState_;
    if (atmo && atmo.skyViewTexture) {
      for (const m of materials) {
        m.setTexture('uAtmoSkyViewLUT', atmo.skyViewTexture);
        m.setVec3('uAtmoSunDir', atmo.sunDir);
        m.setFloat('uAtmoDayFactor', atmo.dayFactor);
        m.setFloat('uAtmoViewHeight', atmo.viewHeightKm);
        m.setVec3('uMoonDir', atmo.moonDir);
        m.setVec3('uShadowLightDir', atmo.lightDir);
      }
    }
  }
}
